/**
 * Design System — single-page brand designer.
 *
 * Mental model:
 *   • Every visit lands on the two-choice screen: "Edit my tenant's brand"
 *     or "Edit another tenant's brand." Selection is purely lifecycle-local;
 *     leaving the page and returning resets to the choice screen.
 *   • Picking a tenant mounts WhiteLabelEditor INLINE — no URL hop, no
 *     chrome change. "Change tenant" returns to the choice screen.
 */

import { useEffect, useMemo, useState } from 'react';
import BrandbookAgentCard from '@/modules/admin/components/BrandbookAgentCard';
import { useFeature } from '@/hooks/useFeature';
import { Trans } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import { Button, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { brand } from '@/brand';
import { useAuth } from '@/hooks/useAuth';
import api from '@/services/api';
import FloatingPreviewWindow from './components/FloatingPreviewWindow';
import { type PreviewSurface } from './components/PreviewSurfaces';
import TenantTree, { type TenantNode } from './components/TenantTree';
import TenantSwitcher, { useTenantSwitcherHotkey } from '@modules/super_admin/console/TenantSwitcher';
import {
  getDesignSystemLandingSeen,
  setDesignSystemLandingSeen,
  getDesignSystemLastTenant,
  setDesignSystemLastTenant,
} from '@modules/super_admin/console/consoleState';
import WhiteLabelEditor, { type EditorView } from './WhiteLabelEditor';
import './components/component-demo.css';

interface TenantSummary {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  parent_tenant_id?: string | null;
}

export default function DesignSystemPanel() {
  // Kept for the rare case (legacy direct links) where we still need to
  // navigate; the new in-page flow does not use it.
  const _navigate = useNavigate();
  void _navigate;
  const { t } = useLanguage();
  const { tenantId: ownTenantId } = useAuth();
  // Same gate the Admin page uses, so the entrance appears in exactly the
  // tenants that can author a bundle.
  const brandbookEnabled = useFeature('modules.brandbook');
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [tenantBrands, setTenantBrands] = useState<Record<string, any>>({});

  // Tenant being designed. The two-choice landing shows on the FIRST visit
  // per session; afterwards the page RESUMES the last edited tenant —
  // "leaving the page and returning resets to the choice screen" was the
  // old behaviour and the #1 friction in jumping between tenants
  // (operator decision 2026-08-27, console redesign §9).
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    () => (getDesignSystemLandingSeen() ? getDesignSystemLastTenant() : null),
  );
  // ⌘K switcher — jump tenants without leaving the editor.
  const [switcherOpen, setSwitcherOpen] = useTenantSwitcherHotkey();
  // WL editor's internal view — hoisted here so the page-level Back
  // button can navigate back ONE step at a time (sub-step → editor →
  // landing) instead of having multiple back buttons on screen.
  const [editorView, setEditorView] = useState<EditorView>('edit');

  // Floating PIP — global pop-out preview. Same component as in the editor.
  const [floatingOpen, setFloatingOpen] = useState(false);
  const [floatingSurface, setFloatingSurface] = useState<PreviewSurface>('home');

  useEffect(() => {
    api.get('/super-admin/tenants', { params: { per_page: 1000 } }).then(res => {
      const list: TenantSummary[] = (res.data?.tenants || []).map((t: any) => ({
        tenant_id: t.tenant_id,
        tenant_name: t.tenant_name,
        tenant_code: t.tenant_code,
        parent_tenant_id: t.parent_tenant_id ?? null,
      }));
      setTenants(list);
    }).catch(err => message.error(`Failed to load tenants: ${err?.message || err}`));
  }, []);

  // Fetch brand overrides for EVERY tenant so the picker tree shows real
  // swatches/logos for the full list, not just the first 30. We batch the
  // requests (10 in flight at a time) so a tenant tree of 200 doesn't
  // open 200 parallel HTTP requests against the API.
  useEffect(() => {
    let cancelled = false;
    const targets = tenants.filter(t => !(t.tenant_id in tenantBrands));
    if (!targets.length) return;
    const BATCH = 10;
    (async () => {
      for (let i = 0; i < targets.length; i += BATCH) {
        if (cancelled) return;
        const slice = targets.slice(i, i + BATCH);
        const results = await Promise.all(slice.map(async t => {
          try {
            const res = await api.get(`/super-admin/tenants/${t.tenant_id}/brand-overrides`);
            return [t.tenant_id, res.data?.brand_overrides || {}] as const;
          } catch {
            return [t.tenant_id, {}] as const;
          }
        }));
        if (cancelled) return;
        const updates = Object.fromEntries(results);
        setTenantBrands(prev => ({ ...prev, ...updates }));
      }
    })();
    return () => { cancelled = true; };
  }, [tenants, tenantBrands]);

  const selectedTenant = useMemo(
    () => tenants.find(t => t.tenant_id === selectedTenantId) ?? null,
    [tenants, selectedTenantId],
  );

  // The "edit another tenant" picker step — also the Back target from the
  // editor, so jumping tenants lands on the remembered tree.
  const [pickerOpen, setPickerOpen] = useState(false);

  function selectTenant(id: string) {
    setSelectedTenantId(id);
    setDesignSystemLastTenant(id);
    setDesignSystemLandingSeen();
    setEditorView('edit');
    // Bump the page back to the top so the editor is in view.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  // Page-level Back behaviour — one step at a time:
  // sub-step (presets / save-as) → editor → PICKER (tree, with its open
  // branches remembered) — not the two-choice landing. Jumping between
  // tenants is choose-again, not start-over.
  function handleEditingBack() {
    if (editorView !== 'edit') {
      setEditorView('edit');
    } else {
      setSelectedTenantId(null);
      setDesignSystemLastTenant(null);
      setPickerOpen(true);
    }
  }
  // Page header subtitle adapts to current view so the user always knows
  // where they are without needing two back buttons.
  function editingSubtitle(): React.ReactNode {
    const tenantLabel = selectedTenant?.tenant_name ?? '…';
    const tenantCode = selectedTenant?.tenant_code;
    // The tenant name is arbitrary data dropped into a Hebrew sentence, so it
    // needs the same bidi isolation the third branch below gives it. <Trans>
    // keeps the name a real element instead of a substring, which is what lets
    // dir="auto" apply to it.
    if (editorView === 'presets') {
      return (
        <Trans
          i18nKey="designSystem.browsingPresetsForTenant"
          values={{ tenant: tenantLabel }}
          components={{ name: <span dir="auto" /> }}
        />
      );
    }
    if (editorView === 'save-as') {
      return (
        <Trans
          i18nKey="designSystem.savingBrandAsPreset"
          values={{ tenant: tenantLabel }}
          components={{ name: <span dir="auto" /> }}
        />
      );
    }
    return (
      <>
        {t('designSystem.designingBrandFor')} <strong dir="auto">{tenantLabel}</strong>
        {tenantCode && <span className="ds-editing-subtitle-code"> · {tenantCode}</span>}
      </>
    );
  }

  /* ─── EDITING MODE — tenant selected, editor mounted inline ─────────── */
  if (selectedTenantId) {
    return (
      <div className="ds-panel ds-panel--editing">
        {/* Header sits at the top of the panel as a flex item — NOT
            sticky. The scroll happens inside ds-editing-scroll below,
            so the browser's scrollbar starts UNDER this header instead
            of running across it. */}
        <div className="ds-sticky-header">
          <div className="ds-sticky-header-row">
            <Button
              type="text"
              icon={<ArrowLeftOutlined className="rtl-flip" />}
              onClick={handleEditingBack}
              className="ds-back-button"
              data-testid="change-tenant"
            >
              {t('common.back')}
            </Button>
            <div className="ds-sticky-header-titles">
              <h1 dir="auto" className="bot-title ds-sticky-title">{t('designSystem.title')}</h1>
              <p className="text-muted ds-sticky-subtitle">
                {editingSubtitle()}
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable content area — owns the page-level scroll for the
            editing view. Scrollbar appears here, BELOW the header. */}
        <div className={`ds-editing-scroll${editorView === 'edit' ? ' wl-editor-host' : ''}`}>
          <WhiteLabelEditor
            tenantId={selectedTenantId}
            key={selectedTenantId}
            view={editorView}
            onViewChange={setEditorView}
          />
        </div>

        <FloatingPreviewWindow
          merged={brand}
          open={floatingOpen}
          onClose={() => setFloatingOpen(false)}
          surface={floatingSurface}
          onSurfaceChange={setFloatingSurface}
        />

        {/* ⌘K — switch the tenant being designed without losing the
            editor context (the WL editor remembers its active section). */}
        <TenantSwitcher
          tenants={tenants}
          currentTenantId={selectedTenantId}
          open={switcherOpen}
          onClose={() => setSwitcherOpen(false)}
          onSelect={selectTenant}
        />
      </div>
    );
  }

  /* ─── GALLERY MODE — no tenant selected ─────────────────────────────── */
  // Build the tree-ready shape (just attach brandOverrides to each node).
  const tenantNodes: TenantNode[] = tenants.map(t => ({
    tenant_id: t.tenant_id,
    tenant_name: t.tenant_name,
    tenant_code: t.tenant_code,
    parent_tenant_id: t.parent_tenant_id ?? null,
    brandOverrides: tenantBrands[t.tenant_id] || {},
  }));

  const ownTenantNode = tenants.find(t => t.tenant_id === ownTenantId);

  /* ─── PICKING MODE — "edit another tenant" selected from landing ─────
   * Renders inline in the same page (no modal) — the user navigates the
   * tree on the full page and can hit "Back to choices" to return. */
  if (pickerOpen) {
    return (
      <div className="ds-panel ds-panel--picking">
        <div className="simple-chat-header">
          <h1 dir="auto" className="bot-title">{t('designSystem.title')}</h1>
          <p className="text-muted">
            {t('designSystem.pickTenantFromTree')}
          </p>
        </div>

        <div className="ds-picker-step">
          <Button
            type="text"
            icon={<ArrowLeftOutlined className="rtl-flip" />}
            onClick={() => setPickerOpen(false)}
            className="ds-back-button"
            data-testid="picker-back"
          >
            {t('superAdmin.backToChoices')}
          </Button>

          <div className="ds-picker-step-body">
            <TenantTree
              tenants={tenantNodes}
              ownTenantId={ownTenantId}
              onSelect={(id) => {
                setPickerOpen(false);
                selectTenant(id);
              }}
            />
          </div>
        </div>


        <FloatingPreviewWindow
          merged={brand}
          open={floatingOpen}
          onClose={() => setFloatingOpen(false)}
          surface={floatingSurface}
          onSurfaceChange={setFloatingSurface}
        />
      </div>
    );
  }

  /* ─── LANDING MODE — two-choice screen ─────────────────────────────── */
  return (
    <div className="ds-panel ds-panel--landing">
      <div className="simple-chat-header">
        <h1 dir="auto" className="bot-title">{t('designSystem.title')}</h1>
        <p className="text-muted">
          {t('designSystem.landingBlurb')}
        </p>
      </div>

      <div className="ds-landing">
        <div className="ds-landing-header">
          <h2 className="ds-landing-title">{t('designSystem.whoseBrand')}</h2>
          <p className="ds-landing-sub">{t('designSystem.landingSub')}</p>
        </div>

        <div className="ds-landing-choices" role="group" aria-label={t('designSystem.pickTenant')}>
          {/* Choice 1 — own tenant. Direct path: skips the picker, opens
              the editor for the platform admin's own tenant. */}
          <button
            type="button"
            className="ds-landing-choice ds-landing-choice--primary"
            onClick={() => ownTenantId && selectTenant(ownTenantId)}
            disabled={!ownTenantId || !ownTenantNode}
            data-testid="edit-own-tenant"
          >
            <span className="ds-landing-choice-title">{t('designSystem.editMyTenantBrand')}</span>
            <span className="ds-landing-choice-sub">
              {ownTenantNode
                ? t('whiteLabel.openForNamedTenant', { tenant: ownTenantNode.tenant_name })
                : t('whiteLabel.openForOwnTenant')}
            </span>
            <span className="ds-landing-choice-cta">{t('designSystem.continueArrow')}</span>
          </button>

          {/* Choice 2 — another tenant. Switches to the in-page picker step. */}
          <button
            type="button"
            className="ds-landing-choice"
            onClick={() => setPickerOpen(true)}
            data-testid="edit-another-tenant"
          >
            <span className="ds-landing-choice-title">{t('designSystem.editAnotherTenantBrand')}</span>
            <span className="ds-landing-choice-sub">
              {t('superAdmin.browseAllTenantsAndPick')}
            </span>
            <span className="ds-landing-choice-cta">{t('designSystem.pickTenantArrow')}</span>
          </button>
        </div>

        {/* The way IN to authoring a bundle. This page is where a super admin
            comes to do brand work, so the entrance has to be here too — it
            lived only on Admin → System Design, and someone looking for it
            on "the design system page" found nothing and reasonably
            concluded the feature was missing (2026-09-23). */}
        {brandbookEnabled && (
          <div className="ds-landing-aside">
            <BrandbookAgentCard />
          </div>
        )}
      </div>

      <FloatingPreviewWindow
        merged={brand}
        open={floatingOpen}
        onClose={() => setFloatingOpen(false)}
        surface={floatingSurface}
        onSurfaceChange={setFloatingSurface}
      />
    </div>
  );
}



